/*
 *  -----------------------------------------------------------------------
 *    <copyright file="create-snapshot.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useRef, useEffect } from 'react';

import { useTranslation, useBoolean } from 'hooks';
import { CREATE_SNAPSHOT, GET_SNAPSHOT_STATUS } from 'queries/snapshots';
import { validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import TextField from 'views/components/core/text-field';

interface ICreateSnapshot {
    open: boolean;
    onClose: Function;
    orgKey: string;
}

const CreateSnapshot = ({ open, onClose, orgKey }: ICreateSnapshot) => {
    const { t } = useTranslation();

    const [createSnapshot] = useMutation(CREATE_SNAPSHOT);
    const [loadSnapshotStatus, { data }] = useLazyQuery(GET_SNAPSHOT_STATUS);

    const [name, setName] = useState('');
    const [errorName, setErrorName] = useState('');

    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);
    const timeout = useRef(null);

    useEffect(() => {
        if (data?.snapshotStatus.isFinished && !data?.snapshotStatus.error) {
            clearInterval(timeout.current);
            saving.setFalse();
            close('success');
        } else if (data?.snapshotStatus.error) {
            clearInterval(timeout.current);
            setErrorName(data?.snapshotStatus.error);
            disabledSave.setTrue();
            saving.setFalse();
        }
    }, [data]);

    const close = (action?: string) => {
        onClose(action);

        if (action !== 'error') {
            setErrorName('');
            setName('');
            saving.setFalse();
            disabledSave.setTrue();
        }
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const validateError = validateFields(value, field, length, required);

        setErrorName(validateError?.name);

        return validateError;
    };

    const onChange = (event: any) => {
        disabledSave.setFalse();
        const { value } = event.target;

        setName(value);
        validate(value, 'name', 60, false);
    };

    const onCreate = () => {
        const errorName = validate(name, 'name', 60, true);
        if (errorName?.name) return;

        saving.setTrue();
        createSnapshot({
            variables: {
                organisationKey: orgKey,
                snapshotName: name,
            },
        })
            .then((result: any) => {
                if (result.data.snapshot) {
                    timeout.current = setInterval(() => {
                        loadSnapshotStatus({
                            variables: {
                                statusId: result.data.snapshot,
                            },
                        });
                    }, 2000);
                }
            })
            .catch((error: any) => {
                console.log('error creating snapshot', error);
                saving.setFalse();
                close('error');
            });
    };

    return (
        <CustomDialog
            className="create-snapshot-dialog"
            onClose={() => close()}
            fullWidth
            scroll="paper"
            maxWidth="sm"
            open={open}
        >
            <DialogTitle id="create-snapshot-modal-title">
                {t('admin:createSnapshot')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content" style={{ padding: 15 }}>
                {!saving.value ? (
                    <TextField
                        className="mb-20"
                        id="snapshot-name"
                        label={`${t('common:name')}*`}
                        value={name}
                        placeholder={`${t('admin:enterSnapshotNamePlaceholder')}*`}
                        fullWidth
                        error={!!errorName}
                        variant="outlined"
                        onChange={onChange}
                        helperText={
                            <CharactersRemaining
                                id="snapshot-name-text"
                                length={name.length}
                                max={60}
                                error={errorName}
                            />
                        }
                    />
                ) : (
                    <p className="loading-state-text">{t('admin:creatingSnapshot')}</p>
                )}
            </DialogContent>
            <DialogActions style={{ padding: 15 }}>
                <SaveButton
                    disabled={disabledSave.value || errorName || saving.value}
                    id="create-snapshot-btn"
                    text={t('common:create')}
                    onClick={onCreate}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default CreateSnapshot;
